/**
 * <s-signup-popup> — opens its signup <dialog> once per visitor after a delay.
 *
 * Attributes:
 *   delay — seconds to wait before opening (default 8)
 *
 * Behaviour:
 *   - Opens straight away when the page re-renders after a signup attempt
 *     (success or error), so the shopper sees the result and their code.
 *   - Otherwise waits `delay` seconds, and never opens over another open dialog.
 *   - With `wait-for`, opens 3 seconds after that popup is closed on the same page.
 *   - Closing it remembers the visitor, so it doesn't open again on later pages.
 *   - Never auto-opens inside the theme editor; opens there while its block is selected.
 *   - Add ?signup-popup=preview to any URL to open it immediately for testing.
 */
const STORAGE_KEY = 'u-unique-signup-popup-seen';
// Pause between closing the popup this one waits for and opening this one.
const AFTER_PREVIOUS_DELAY = 3000;

class SSignupPopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    dialog.addEventListener('close', () => this.#remember(), { signal: this.#ac.signal });

    // Optional teaser tab: stays in the corner after the popup is closed and reopens it.
    const teaser = this.querySelector('[data-popup-teaser]');
    if (teaser) {
      const opts = { signal: this.#ac.signal };
      dialog.addEventListener('close', () => { teaser.hidden = false; }, opts);
      teaser.addEventListener('click', () => {
        teaser.hidden = true;
        if (!dialog.open) dialog.showModal();
      }, opts);
      if (this.#seen()) teaser.hidden = false;
    }

    const hasResult = this.querySelector('[data-signup-success], [role="alert"]');
    if (hasResult) {
      this.#remember();
      if (!dialog.open) dialog.showModal();
      return;
    }

    // Preview: ?signup-popup=preview opens it straight away, ignoring "already seen".
    const preview = new URLSearchParams(window.location.search).get(this.getAttribute('preview-param') || 'signup-popup') === 'preview';
    if (preview) {
      this.#timer = setTimeout(() => {
        if (!dialog.open) dialog.showModal();
      }, 0);
      return;
    }

    // In the editor, open while the popup block is selected instead of on a timer.
    if (window.Shopify && window.Shopify.designMode) {
      const opts = { signal: this.#ac.signal };
      document.addEventListener('shopify:block:select', (e) => {
        if (e.target instanceof Node && this.contains(e.target) && !dialog.open) dialog.showModal();
      }, opts);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (e.target instanceof Node && this.contains(e.target) && dialog.open) dialog.close();
      }, opts);
      return;
    }

    if (this.#seen()) return;
    const waitFor = this.getAttribute('wait-for');
    if (waitFor) {
      let ready = false;
      try { ready = window.localStorage.getItem(waitFor) === '1'; } catch { /* ignore */ }
      if (!ready) {
        // Open shortly after the popup this one waits for is closed, on the same page.
        document.addEventListener('s-signup-popup:seen', (e) => {
          if (e.detail?.key !== waitFor || this.#seen()) return;
          clearTimeout(this.#timer);
          this.#timer = setTimeout(() => {
            if (dialog.open || document.querySelector('dialog[open]')) return;
            dialog.showModal();
          }, AFTER_PREVIOUS_DELAY);
        }, { signal: this.#ac.signal });
        return;
      }
    }

    const delaySeconds = Number(this.getAttribute('delay'));
    const delay = (Number.isFinite(delaySeconds) && delaySeconds >= 0 ? delaySeconds : 8) * 1000;

    this.#timer = setTimeout(() => {
      if (dialog.open || document.querySelector('dialog[open]')) return;
      dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #seen() {
    try {
      return window.localStorage.getItem(this.getAttribute('storage-key') || STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #remember() {
    const key = this.getAttribute('storage-key') || STORAGE_KEY;
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      /* storage unavailable: the popup may show again, which is harmless */
    }
    document.dispatchEvent(new CustomEvent('s-signup-popup:seen', { detail: { key } }));
  }
}

if (!customElements.get('s-signup-popup')) {
  customElements.define('s-signup-popup', SSignupPopup);
}
